if (!customElements.get('slynara-site-header')) {
  customElements.define(
    'slynara-site-header',
    class SlynaraSiteHeader extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section');
        this.header = this.querySelector('[data-slynara-header]');
        this.mobileNav = this.querySelector('[data-mobile-nav]');
        this.mobilePanel = this.mobileNav?.querySelector('.slynara-mobile-nav__panel');
        this.openButton = this.querySelector('[data-mobile-open]');
        this.closeButtons = this.querySelectorAll('[data-mobile-close]');
        this.megaMenus = [...this.querySelectorAll('[data-mega-menu]')];
        this.disclosures = [...this.querySelectorAll('details')];
        this.lastScroll = window.scrollY;
        this.openElement = null;
        this.desktopQuery = window.matchMedia('(min-width: 1200px)');
        this.hoverTimers = new Map();
        this.onBreakpoint = () => {
          this.clearHoverTimers();
          if (this.desktopQuery.matches) this.closeMobileNav({ immediate: true });
          this.setHeaderHeight();
        };
        this.onInteraction = () => {
          if (this.hasHeaderInteraction()) this.section?.classList.remove('is-hidden');
        };

        this.onOpen = () => this.openMobileNav();
        this.onClose = () => this.closeMobileNav();
        this.onKeydown = (event) => this.handleKeydown(event);
        this.onScroll = () => this.handleScroll();
        this.onDocumentClick = (event) => this.handleDocumentClick(event);
        this.onDisclosureToggle = (event) => {
          const disclosure = event.target;
          if (!(disclosure instanceof HTMLDetailsElement)) return;
          disclosure.querySelector(':scope > summary')?.setAttribute('aria-expanded', disclosure.open.toString());
          if (disclosure.matches('[data-mega-menu]') && disclosure.open) {
            this.megaMenus.forEach((menu) => {
              if (menu !== disclosure) menu.removeAttribute('open');
            });
          }
          this.onInteraction();
        };

        this.onMenuEnter = (event) => {
          if (event.pointerType !== 'mouse' || !this.desktopQuery.matches) return;
          const menu = event.currentTarget;
          window.clearTimeout(this.hoverTimers.get(menu));
          this.hoverTimers.set(menu, window.setTimeout(() => {
            this.hoverTimers.delete(menu);
            menu.open = true;
          }, 100));
        };
        this.onMenuLeave = (event) => {
          if (event.pointerType !== 'mouse' || !this.desktopQuery.matches) return;
          this.scheduleMenuClose(event.currentTarget);
        };
        this.onMenuFocusOut = (event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) this.scheduleMenuClose(event.currentTarget);
        };
        this.onMenuClick = () => this.clearHoverTimers();
        this.megaMenus.forEach((menu) => {
          menu.addEventListener('pointerenter', this.onMenuEnter);
          menu.addEventListener('pointerleave', this.onMenuLeave);
          menu.addEventListener('focusout', this.onMenuFocusOut);
          menu.addEventListener('click', this.onMenuClick);
        });
        this.openButton?.addEventListener('click', this.onOpen);
        this.closeButtons.forEach((button) => button.addEventListener('click', this.onClose));
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('focusin', this.onInteraction);
        this.addEventListener('toggle', this.onDisclosureToggle, true);
        this.desktopQuery.addEventListener('change', this.onBreakpoint);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.setHeaderHeight());
        if (this.section) this.resizeObserver.observe(this.section);
        this.handleScroll();
        this.setHeaderHeight();
      }

      disconnectedCallback() {
        this.clearHoverTimers();
        this.megaMenus.forEach((menu) => {
          menu.removeEventListener('pointerenter', this.onMenuEnter);
          menu.removeEventListener('pointerleave', this.onMenuLeave);
          menu.removeEventListener('focusout', this.onMenuFocusOut);
          menu.removeEventListener('click', this.onMenuClick);
        });
        this.openButton?.removeEventListener('click', this.onOpen);
        this.closeButtons.forEach((button) => button.removeEventListener('click', this.onClose));
        this.removeEventListener('keydown', this.onKeydown);
        this.removeEventListener('focusin', this.onInteraction);
        this.removeEventListener('toggle', this.onDisclosureToggle, true);
        this.desktopQuery.removeEventListener('change', this.onBreakpoint);
        this.closeMobileNav({ immediate: true, restoreFocus: false });
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      clearHoverTimers() {
        this.hoverTimers.forEach((timer) => window.clearTimeout(timer));
        this.hoverTimers.clear();
      }

      scheduleMenuClose(menu) {
        window.clearTimeout(this.hoverTimers.get(menu));
        this.hoverTimers.set(menu, window.setTimeout(() => {
          this.hoverTimers.delete(menu);
          if (!menu.matches(':hover') && !menu.contains(document.activeElement)) menu.open = false;
        }, 100));
      }

      setHeaderHeight() {
        if (this.section) document.documentElement.style.setProperty('--header-height', `${this.section.offsetHeight}px`);
      }

      handleScroll() {
        const currentScroll = window.scrollY;
        const pastHeader = currentScroll > (this.offsetHeight || 64);
        this.header?.classList.toggle('is-scrolled', currentScroll > 8);

        if (this.dataset.sticky === 'on-scroll-up') {
          this.section?.classList.toggle('is-sticky', pastHeader);
          this.section?.classList.toggle('is-hidden', pastHeader && currentScroll > this.lastScroll && !this.hasHeaderInteraction());
        } else if (this.dataset.sticky === 'always') {
          this.section?.classList.add('is-sticky');
        }

        this.lastScroll = Math.max(0, currentScroll);
      }

      hasHeaderInteraction() {
        return (this.mobileNav && !this.mobileNav.hidden) ||
          this.querySelector('.header__search details[open], [data-mega-menu][open]') ||
          this.contains(document.activeElement);
      }

      cancelMenuCallbacks() {
        window.cancelAnimationFrame(this.openFrame);
        window.clearTimeout(this.focusTimer);
        window.clearTimeout(this.closeTimer);
      }

      isVisible(element) {
        if (!element?.isConnected || !element.getClientRects().length ||
          element.closest('[hidden], [inert]') || getComputedStyle(element).visibility !== 'visible') return false;
        for (let parent = element.parentElement; parent; parent = parent.parentElement) {
          if (parent.matches('details:not([open])') &&
            !parent.querySelector(':scope > summary')?.contains(element)) return false;
        }
        return true;
      }

      openMobileNav() {
        if (!this.mobileNav || this.desktopQuery.matches) return;
        this.cancelMenuCallbacks();
        if (!this.mobileNav.contains(document.activeElement)) this.openElement = document.activeElement;
        this.mobileNav.hidden = false;
        this.openFrame = requestAnimationFrame(() => this.mobileNav.classList.add('is-open'));
        this.openButton?.setAttribute('aria-expanded', 'true');
        if (!document.documentElement.classList.contains('slynara-lock-scroll')) {
          document.documentElement.classList.add('slynara-lock-scroll');
          this.ownsScrollLock = true;
        }
        this.onInteraction();
        this.focusTimer = window.setTimeout(() => this.mobilePanel?.focus(), 20);
      }

      closeMobileNav({ immediate = false, restoreFocus = true } = {}) {
        this.cancelMenuCallbacks();
        if (!this.mobileNav) return;
        const wasVisible = !this.mobileNav.hidden;
        this.mobileNav.classList.remove('is-open');
        this.openButton?.setAttribute('aria-expanded', 'false');
        if (this.ownsScrollLock) {
          document.documentElement.classList.remove('slynara-lock-scroll');
          this.ownsScrollLock = false;
        }
        const finish = () => {
          const focusInside = this.mobileNav.contains(document.activeElement);
          this.mobileNav.hidden = true;
          this.mobileNav.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
          if (!restoreFocus || !wasVisible) return;
          if (immediate && !focusInside) return;
          const target = this.isVisible(this.openElement) ? this.openElement :
            this.querySelector('.slynara-header__logo-link');
          if (this.isVisible(target)) target.focus();
        };
        if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
        else this.closeTimer = window.setTimeout(finish, 230);
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          this.clearHoverTimers();
          if (this.mobileNav?.classList.contains('is-open')) this.closeMobileNav();
          this.megaMenus.forEach((menu) => {
            if (!menu.open) return;
            const restoreFocus = menu.contains(document.activeElement);
            menu.removeAttribute('open');
            if (restoreFocus) menu.querySelector(':scope > summary')?.focus();
          });
          return;
        }

        if (event.key !== 'Tab' || (!this.mobileNav || this.mobileNav.hidden)) return;
        const focusable = [...this.mobilePanel.querySelectorAll('a[href], button, summary, input, select, textarea, [tabindex]')]
          .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled, [aria-disabled="true"]') && this.isVisible(element));
        if (!focusable.length) {
          event.preventDefault();
          this.mobilePanel.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (document.activeElement === this.mobilePanel) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      handleDocumentClick(event) {
        this.clearHoverTimers();
        this.megaMenus.forEach((menu) => {
          if (menu.open && !menu.contains(event.target)) menu.removeAttribute('open');
        });
      }
    }
  );
}
